import React, { useContext, useState } from 'react';
import { Dropdown, Grid, Header, Icon, Popup } from 'semantic-ui-react';

import {
    ClippedContent,
    ConfirmationPrompt,
    Editable,
    generateResolver,
    Notification,
    STATUS_TYPES,
    VALIDATORS
} from 'dyl-components';
import Avatar from 'react-avatar';

import { Controller, useForm } from 'react-hook-form';
import accountActions from 'actions/account';
import archiveAction from 'actions/archive';
import { useDispatch, useSelector } from 'react-redux';
import ErrorPopup from 'shared/ErrorPopup';
import SocialMedia from 'shared/SocialMedia';
import ConversionControls from "./ConversionControls";
import Controls from "./Controls";
import SubControls from "./SubControls";
import { FAVORITE_TYPE } from 'shared/constants/FAVORITE_TYPE';
import { ProfileFavoriteIcon } from 'shared/ProfileFavoriteIcon';
import StagesSection from './StagesSection';
import { USER_PERMISSIONS } from 'dyl-components';
import RestrictedView from 'shared/RestrictedView';

import { ArchivedRecordsContext } from 'shared/context/ArchivedRecordsProvider';
import { useNavigate } from 'react-router-dom';

function copy(text) {
    return (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(text);
    };
}

export const ProfileHeader = ({
    name,
    id,
    breadcrumbs,
    isSidePanelOpen,
    assignees
}) => {
    const [isEditing, setEditing] = useState(false);
    const isDyl = useSelector(state => state.auth.dyl_employee);
    const navigate = useNavigate()

    const { control, formState: { isValid, isDirty }, handleSubmit, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            name: name || ''
        },
        resolver: generateResolver({
            name: VALIDATORS.BUSINESS_NAME().required('This field is required')
        })
    });

    const onEdit = () => {
        reset();
        setEditing(true);
    }

    const onCancelEdit = () => {
        setEditing(false);
    }

    const { account, isUpdating, customer_information, contactsForPinning, officeViewAccount } = useSelector(state => ({
        account: state.account.account,
        isUpdating: state.account.isUpdatingAccount,
        customer_information: state.account.account?.customer || {},
        contactsForPinning: state.account.contactsForPinning,
        officeViewAccount: state.officeView.contact_lookup?.account_id,
    }))

    const onConfirmEdit = async (data) => {
        try {
            await dispatch(accountActions.updateAccount(id, { ...data, pinned: account.pinned.map(contact => contact.id), primary_contact_id: account.primary_contact.id }));
            Notification.alert('Successfully updated account!', STATUS_TYPES.SUCCESS);
            dispatch(accountActions.readAccount(id));
        } catch {
            Notification.alert('Failed to update account', STATUS_TYPES.ERROR);
        }
    }

    const account_type = account?.account_type;
    const master_account = account?.master_account?.id
    const pipeline = account?.customer?.customer_pipeline

    const { customer_status, fund_type } = customer_information;

    const isUnfunded = fund_type === "unfunded"
    const dispatch = useDispatch();

    const popupRef = React.useRef();

    const [openForm, setOpenForm] = useState(null);
    const [isArchivePopupOpen, setIsArchivePopupOpen] = useState({
        open: false,
        type: null,
    });
    const isAccountOnly = account?.counts?.contact_count === 0
    const accountDropdownItems = [
        ...(account_type === "business"
            ? master_account
                ? [
                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.MASTER_ACCOUNT_EDIT}>
                        <Dropdown.Item
                            key="change-master"
                            onClick={() => setOpenForm("link-master")}
                            icon={<Icon className="fas fa-buildings" />}
                            content="Change Master Account"
                        />
                    </RestrictedView>,
                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.MASTER_ACCOUNT_EDIT}>
                        <Dropdown.Item
                            key="unlink-master"
                            onClick={() => setOpenForm("unlink-master")}
                            icon={<Icon className="fas fa-diagram-nested" />}
                            content="Unlink Master Account"
                        />
                    </RestrictedView>,
                ]
                : [
                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.MASTER_ACCOUNT_EDIT}>
                        <Dropdown.Item
                            key="link-master"
                            onClick={() => setOpenForm("link-master")}
                            icon={<Icon className="fas fa-diagram-nested" />}
                            content="Link Master Account"
                        />
                    </RestrictedView>,
                ]
            : []),

        ...((customer_status === "active" || customer_status === "paused" )&& account_type !== "household"
            ? pipeline?.stages?.length > 0
                ? [            
                    <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} type="restricted" assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]} userRestrict>
                        <Dropdown.Item
                            key="change-pipeline"
                            onClick={() => setOpenForm("change-pipeline")}
                        >
                            Change Pipeline
                        </Dropdown.Item>
                    </RestrictedView>,
                    <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} type="restricted" assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]} userRestrict>
                        <Dropdown.Item
                            key="close-pipeline"
                            onClick={() => setOpenForm("close-pipeline")}
                        >
                            Close Pipeline
                        </Dropdown.Item>
                    </RestrictedView>,
                ]
                : [
                    <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} type="restricted" assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]} userRestrict>
                        <Dropdown.Item
                            key="add-pipeline"
                            onClick={() => setOpenForm("add-pipeline")}
                            icon={<Icon className="fas fa-filter" />}
                            content="Add Pipeline"
                        />
                    </RestrictedView>,
                ]
            : []),
        <RestrictedView userPermission={USER_PERMISSIONS.ACCOUNT_DELETE} type="disabled" assignmentIds={[(assignees?.owner_id || 0), (assignees?.coowner_id || 0)]} userRestrict>
             {/* TODO: For future, DYL account being able to archive an unfunded customer  && (!isUnfunded || !isDyl) */}
             {(customer_status && customer_status !== "canceled") ? (
                <Popup
                    content={"Active customer"}
                    position="left center"
                    inverted
                    on={'hover'}
                    trigger={
                        <Dropdown.Item 
                            className={`account-archive-${
                                (customer_status && customer_status !== "canceled") ? "disable" : ""
                            }`} 
                            icon={<Icon className="fas fa-archive" />}
                            content="Archive"
                            key="archive"
                        />
                    }
                />
            ) : (
                <Dropdown.Item
                    onClick={() => {

                    }}
                    as={Dropdown}
                    size="tiny"
                    trigger={<>
                        <Icon className="fas fa-archive" disabled={(customer_status && customer_status !== "canceled")} />
                        Archive
                    </>}
                >
                    <Dropdown.Menu>
                        {account?.has_contact_with_modules ? (
                            <Popup
                                content={"Contact module applied"}
                                position="left center"
                                inverted
                                on={'hover'}
                                trigger={
                                    <Dropdown.Item 
                                        className={`account-archive-${
                                            account?.has_contact_with_modules ? "disable" : ""
                                        }`} 
                                        content="Account Only"
                                        key="accountOnly"
                                    />
                                }
                            />
                        ) : (
                            <Dropdown.Item onClick={() => {
                                setIsArchivePopupOpen({ type: "account", open: true })
                            }}>
                                Account Only
                            </Dropdown.Item>
                        )}
                        {isAccountOnly ? (
                            <Popup
                                content={"Account only"}
                                position="left center"
                                inverted
                                on={'hover'}
                                trigger={
                                    <Dropdown.Item 
                                        className={`account-archive-${
                                            isAccountOnly ? "disable" : ""
                                        }`} 
                                        content="Account & Contacts"
                                        key="AccountContacts"
                                    />
                                }
                            />
                        ) : (
                            <Dropdown.Item
                                onClick={() => {
                                    setIsArchivePopupOpen({ type: "account-and-contacts", open: true })
                                }}
                            >
                            Account & Contacts
                        </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown.Item>
            )}
        </RestrictedView>
    ];
    const { isArchivedView } = useContext(ArchivedRecordsContext);
    const onArchiveAccountConfirm = async () => {
        try {
            await dispatch(archiveAction.archiveRecords(undefined,{
                account_id: id,
                contacts: isArchivePopupOpen.type === "account" ? [] : contactsForPinning?.map(contact => ({
                    contact_id: contact.id,                    
                }))
            }));
            Notification.alert(`Successfully archived account${isArchivePopupOpen.type !== "account" ? " & contacts" : ""}!`, STATUS_TYPES.SUCCESS, true);
            navigate("/")
        } catch (error) {
            Notification.alert(
                "Failed to archive account",
                STATUS_TYPES.ERROR
            );
        }
    }
    return (
        <React.Fragment>
            <div style={{ paddingBottom: "0.5em" }}>
                {breadcrumbs}
            </div>
            <Grid>
                <Grid.Row columns='equal' verticalAlign='middle'>
                    <Grid.Column width={isArchivedView ? 16 : 6}>
                        <Grid columns='equal' verticalAlign='middle'>
                            <Grid.Column className='ProfileHeader__avatarContainer' width={isSidePanelOpen ? 3 : 2} textAlign='center' style={{ padding: '1em 0 1em 0' }}>
                                <Avatar
                                    size='5em'
                                    round
                                    maxInitials={2}
                                    name={name}
                                />
                                <SocialMedia record_type="account" />
                            </Grid.Column>
                            <Grid.Column>
                                <Header as='h3' style={{ marginBottom: 5 }}>
                                    {!isEditing ?
                                        <div onClick={!isArchivedView && onEdit}>
                                            <ClippedContent>
                                                {name}
                                            </ClippedContent>
                                        </div> : (
                                            <Editable
                                                value={name}
                                                isEditing={isEditing}
                                                onCancelEdit={onCancelEdit}
                                                onEdit={onEdit}
                                                size='small'
                                                underline
                                                onConfirmEdit={handleSubmit(onConfirmEdit, (errors, e) => {
                                                    console.log(errors, e);
                                                })}
                                                recordType="account"
                                                assignees={account.assignees}
                                                submitDisabled={!isDirty || !isValid || isUpdating}
                                            >
                                                <Controller
                                                    name='name'
                                                    control={control}
                                                    render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                                                        <ErrorPopup
                                                            isPopupOpen={typeof (error?.message) != "undefined"}
                                                            message={error?.message}
                                                            inputName={name}
                                                            inputValue={value}
                                                            onInputChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                                            inputPlaceholder='Enter new name'
                                                            isLoading={isUpdating}
                                                        />
                                                    )}
                                                />
                                            </Editable>
                                        )}
                                </Header>
                                <div className='ProfileHeader__leftContainer'>
                                    <span><Icon className={`fas${account_type === 'business' ? ' fa-building' : ''}${account_type === 'household' ? ' fa-house' : ''}`} />Account</span>
                                    <div className='ProfileHeader__iconsContainer'>
                                        <ProfileFavoriteIcon id={id} type={FAVORITE_TYPE.ACCOUNT} />
                                    </div>
                                </div>
                                <div>
                                    ID: {isArchivedView ? id : <Popup
                                        trigger={<a href='#?' role="button" tabIndex="0" onClick={copy(id)}>{id} <Icon className='fas fa-copy' /></a>}
                                        content='Copied!'
                                        on='click'
                                        closeOnTriggerMouseLeave
                                        position='bottom center'
                                        inverted />}
                                </div>
                            </Grid.Column>
                        </Grid>
                    </Grid.Column>
                    {customer_status && !isArchivedView && (
                        <Grid.Column width={6}>
                            {(!isUnfunded || !isDyl) && <div className='ProfileHeader__PipelineConversion'>
                                <ConversionControls />
                            </div>}
                            <StagesSection 
                                id={id} 
                                assignees={assignees}
                            />
                        </Grid.Column>
                    )}
                    {!isArchivedView && <Grid.Column textAlign='right'>
                        <Grid columns='equal' verticalAlign='top' textAlign='left'>
                            {!customer_status && !isUnfunded ? <Grid.Column width={isSidePanelOpen ? 11 : 12} textAlign='right'> <ConversionControls /> </Grid.Column> : <Grid.Column width={isSidePanelOpen ? 5 : 7} textAlign='right'></Grid.Column>}

                            <Grid.Column textAlign='left' >

                                <Controls popupRef={popupRef} openForm={openForm} setOpenForm={setOpenForm} />

                                <SubControls
                                    popupRef={popupRef}
                                    dropdownItems={accountDropdownItems}
                                    record_id={id}
                                    hasVoicemail={account?.voicemail}
                                    isAccount
                                    refresh={() => {
                                        dispatch(accountActions.readAccount(id));
                                        if (officeViewAccount && Number(officeViewAccount) === Number(id)) {
                                            dispatch(accountActions.viewLookup(officeViewAccount));
                                        }
                                    }}
                                />
                            </Grid.Column>
                        </Grid>
                    </Grid.Column>}
                </Grid.Row>
            </Grid>
            <Popup
                open={isArchivePopupOpen.open}
                position={`bottom right`}
                context={popupRef}
                onClose={() => setIsArchivePopupOpen({ open: false, type: null })}
                content={(<ConfirmationPrompt
                    header={<div style={{ paddingTop: '1em', marginBottom: '5px' }}>Are you sure?</div>}
                    subheader={
                        <div style={{ marginBottom: '-1em' }}>
                            Archive closes/removes all activities/contacts associated with this record.
                        </div>
                    }
                    onConfirm={onArchiveAccountConfirm}
                    onCancel={() => { setIsArchivePopupOpen({ open: false, type: null }) }}
                />)}
            />
        </React.Fragment>
    );
};
